import React, { useCallback, useContext, useEffect, useMemo } from 'react'
import { useIntl } from 'react-intl'
import CartReviewTile from 'components/Pages/CartReviewPage/CartReviewTile'
import CartReviewBanner from 'components/Pages/CartReviewPage/CartReviewBanner'
import useUserMultiCartSubscriptionItemData from '../hooks/useUserMultiCartSubscriptionItemData'
import LuxPlusProductPlaceholder from 'luxPlus/components/LuxPlusProductPlaceholder'
import BodyText from 'LuxKit/Typography/BodyText'
import FormatDateTime from 'components/utils/Formatters/FormatDateTime'
import { addYears } from 'lib/datetime/dateUtils'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import TextLink from 'LuxKit/TextLink'
import LuxPlusSubscriptionCancellationModal from 'luxPlus/components/LuxPlusSubscriptionCancellationModal'
import ModalContext from 'contexts/ModalContext'
import type { CartItemData } from '../hooks/useAllUserMultiCartItemsMetadata'
import { calculateHotelLuxPlusDetails, filterOutAddonItems } from 'components/Pages/CartReviewPage/CartReviewUtils'
import Places from 'constants/places'
import { getSearchQueryFromFilter } from 'lib/search/searchUtils'
import LuxPlusCartReviewPriceStack from 'components/Pages/CartReviewPage/LuxPlusCartReviewPriceStack'
import CondensedCartReviewTile from 'components/Pages/CartReviewPage/CondensedCartReviewTile'
import CondensedCartReviewTileContent from 'components/Pages/CartReviewPage/CondensedCartReviewTileContent'
import CartReviewTileContent from 'components/Pages/CartReviewPage/CartReviewTileContent'
import LoadingCartReviewTileSkeleton from 'components/Pages/CartReviewPage/LoadingCartReviewTileSkeleton'
import CartReviewTileActions from 'components/Pages/CartReviewPage/CartReviewTileActions'
import { MultiCartReviewMachineContext } from '../MultiCartReviewMachineContext'
import UndoDeletedItemsTile from '../UndoDeletedItemsTile'
import { useSelector } from '@xstate/react'
import { MultiCartContext } from 'machines/cart/MultiCartMachineProvider'
import MultiCartParticipantLabel from './MultiCartParticipantLabel'

const memberPriceFilter: App.OfferListFilters = {
  offerTypes: ['hotel', 'tactical_ao_hotel'],
  luxPlusFeatures: ['Member prices'],
  destinations: [Places.Anywhere.id],
  sortBy: 'luxPlusList',
}

const searchLink = `/search?${getSearchQueryFromFilter(memberPriceFilter)}`

interface Props {
  multiCartItem: App.MultiCartLuxPlusSubscriptionItem
  cartItemsData: Array<CartItemData>
  /**
   * @default default
   */
  tileVariant?: 'default' | 'condensed'
}

const renewalDate = addYears(new Date(), 1)

function LuxPlusMultiCartReviewTile({ multiCartItem, cartItemsData, tileVariant = 'default' }: Props) {
  const intl = useIntl()
  const showModal = useContext(ModalContext)
  const data = useUserMultiCartSubscriptionItemData()
  const multiCartActorRef = MultiCartContext.useActorRef()
  const multiCartReviewActorRef = MultiCartReviewMachineContext.useActorRef()
  const activeCartId = useSelector(multiCartActorRef, (snapshot) => snapshot.context.activeCartId)

  const isDeleted = multiCartItem.state === 'deleted'
  const isSavedForLater = multiCartItem.state === 'save_for_later'

  useEffect(() => {
    // when luxplus cart review tile data failed to load, save for later
    // Only process if item is in 'active' state to prevent re-triggering
    if (
      !!data.error &&
      !data.loading &&
      multiCartItem.state === 'active' && // Only process active items
      activeCartId
    ) {
      multiCartReviewActorRef.send({
        type: 'update save items for later',
        cartId: activeCartId,
        itemIds: [multiCartItem.id],
        saveForLater: true,
        skipNotification: true,
      })
    }
  }, [data.error, data.loading, multiCartItem.state, activeCartId, multiCartItem.id, multiCartReviewActorRef])

  const handleSaveForLater = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    multiCartReviewActorRef.send({
      type: 'update save items for later',
      cartId: activeCartId!,
      itemIds: [multiCartItem.id],
      saveForLater: !(multiCartItem.state === 'save_for_later'),
    })
  }

  const handleDelete = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()

    multiCartActorRef.send({
      type: 'delete items from given cart',
      cartId: activeCartId!,
      itemIds: [multiCartItem.id],
      currentStates: {
        [multiCartItem.id]: multiCartItem.state,
      },
    })
  }

  const { totalSavings } = useMemo(() => {
    return calculateHotelLuxPlusDetails(filterOutAddonItems(cartItemsData))
  }, [cartItemsData])

  const onShowCancellationPolicy = useCallback<React.MouseEventHandler<HTMLAnchorElement>>((event) => {
    event.stopPropagation()
    event.preventDefault()
    showModal(<LuxPlusSubscriptionCancellationModal />)
  }, [showModal])

  const membershipTitle = intl.formatMessage({
    id: '8HmzZfr0Uf',
    defaultMessage: 'LuxPlus+ Yearly Membership',
    description: 'Title for the LuxPlus+ yearly membership cart item',
  })

  const cancelRenewalLabel = intl.formatMessage({
    id: '+eZ919vI9T',
    defaultMessage: 'Cancel renewal anytime',
    description: 'Link text for viewing LuxPlus+ subscription cancellation policy',
  })

  if (data.loading) {
    return <LoadingCartReviewTileSkeleton />
  }

  if (isDeleted) {
    return <UndoDeletedItemsTile
      title={membershipTitle}
      onUndoClick={() => {
        multiCartActorRef.send({
          type: 'undo delete',
          itemIds: [multiCartItem.id],
        })
      }}
    />
  }

  if (tileVariant === 'condensed') {
    return <CondensedCartReviewTile
      to="/lux-plus"
      imageElement={<LuxPlusProductPlaceholder />}
      content={<CondensedCartReviewTileContent />}
      price={<LuxPlusCartReviewPriceStack
        price={data.itemTotals?.price ?? 0}
        joiningFee={data.joiningFee?.amount ?? 0}
        waiveFee={!!data.joiningFee?.waived}
        tileVariant="condensed"
      />}
      banner={<CartReviewBanner
        variant={(!isDeleted && isSavedForLater) ? 'luxplus' : undefined}
        savings={totalSavings}
        to={searchLink}
      />}
      onDelete={handleDelete}
    />
  }

  return <CartReviewTile
    to="/lux-plus"
    content={<CartReviewTileContent
      title={membershipTitle}
      metaData={<BodyText variant="medium" colour="neutral-two">
        {intl.formatMessage(
          {
            id: 'ciCixWuMEX',
            defaultMessage: 'Renews {renewalDate}',
            description: 'Renewal date text for LuxPlus+ yearly membership',
          },
          { renewalDate: <FormatDateTime value={renewalDate} format="dmy-casual-short" /> },
        )}
      </BodyText>}
      cancellationPolicy={<TextLink weight="bold" colour="highlight-secondary" size="medium" endIcon={<LineInfoCircleIcon colour="neutral-one" />} onClick={onShowCancellationPolicy}>
        {cancelRenewalLabel}
      </TextLink>}
      label={<MultiCartParticipantLabel item={multiCartItem} />}
    />}
    imageElement={<LuxPlusProductPlaceholder />}
    price={<LuxPlusCartReviewPriceStack
      price={data.itemTotals?.price ?? 0}
      joiningFee={data.joiningFee?.amount ?? 0}
      waiveFee={!!data.joiningFee?.waived}
      tileVariant="default"
    />}
    banner={<CartReviewBanner
      variant={(!isDeleted && isSavedForLater) ? 'luxplus' : undefined}
      savings={totalSavings}
      to={searchLink}
    />}
    isBookable
    saveForLater={isSavedForLater}
    actions={<CartReviewTileActions
      isBookable
      saveForLater={isSavedForLater}
      onSaveForLater={handleSaveForLater}
      onDelete={handleDelete}
    />}
  />
}

export default LuxPlusMultiCartReviewTile
